import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type PickerSide = 'top' | 'bottom' | 'left' | 'right';

/** Mirrors the `collisionPadding` and the design system's `sideOffset`. */
const GUTTER = 12;
/** Default cap, kept in sync with the `--picker-max-h` fallback. */
const DESIRED_HEIGHT = 452;
/** Below this the dropdown is unusable, so stop shrinking and let it scroll. */
const MIN_HEIGHT = 160;

interface VerticalSpace {
  above: number;
  below: number;
}

const measureSpace = (
  trigger: HTMLElement,
  boundary: Element | null | undefined,
): VerticalSpace => {
  const triggerRect = trigger.getBoundingClientRect();
  const boundaryRect = boundary?.getBoundingClientRect();
  const hasBoundary = !!boundaryRect && boundaryRect.height > 0;

  const top = hasBoundary ? Math.max(boundaryRect.top, 0) : 0;
  const bottom = hasBoundary
    ? Math.min(boundaryRect.bottom, window.innerHeight)
    : window.innerHeight;

  return {
    above: triggerRect.top - top - GUTTER,
    below: bottom - triggerRect.bottom - GUTTER,
  };
};

const heightFor = (space: VerticalSpace, side: PickerSide) =>
  Math.max(side === 'top' ? space.above : space.below, MIN_HEIGHT);

interface UsePickerPlacementOptions {
  triggerRef: RefObject<HTMLElement>;
  open: boolean;
  boundary: Element | null | undefined;
  /** Side forced by the caller; when set it is always honoured. */
  forcedSide: PickerSide | undefined;
}

/**
 * Picks the side the dropdown opens on from the room available around the
 * trigger, once per opening, and keeps it until the dropdown closes. Radix
 * re-runs its own collision check on every content resize, so filtering the
 * list used to flip the menu from one side of the trigger to the other
 * mid-interaction. Capping the height to the room on the chosen side is what
 * keeps that check from firing at all.
 */
export const usePickerPlacement = ({
  triggerRef,
  open,
  boundary,
  forcedSide,
}: UsePickerPlacementOptions) => {
  const isVerticalForced = forcedSide === 'top' || forcedSide === 'bottom';
  const isSideResolvable = !forcedSide || isVerticalForced;

  const [side, setSide] = useState<PickerSide>(forcedSide ?? 'bottom');
  const [availableHeight, setAvailableHeight] = useState<number>();
  const sideRef = useRef<PickerSide>(side);
  const isResolvedRef = useRef(false);

  const resolvePlacement = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || !isSideResolvable) return;

    const space = measureSpace(trigger, boundary);
    let nextSide: PickerSide = forcedSide ?? 'bottom';
    if (!forcedSide && space.below < Math.min(DESIRED_HEIGHT, space.above)) {
      nextSide = 'top';
    }

    isResolvedRef.current = true;
    sideRef.current = nextSide;
    setSide(nextSide);
    setAvailableHeight(heightFor(space, nextSide));
  }, [triggerRef, boundary, forcedSide, isSideResolvable]);

  useLayoutEffect(() => {
    if (!open) {
      isResolvedRef.current = false;
      return;
    }
    if (isResolvedRef.current) return;
    resolvePlacement();
  }, [open, resolvePlacement]);

  useEffect(() => {
    if (!open || !isSideResolvable) return;

    const updateHeight = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      setAvailableHeight(
        heightFor(measureSpace(trigger, boundary), sideRef.current),
      );
    };

    window.addEventListener('resize', updateHeight);
    window.addEventListener('scroll', updateHeight, true);
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('scroll', updateHeight, true);
    };
  }, [open, boundary, triggerRef, isSideResolvable]);

  return { side, availableHeight, resolvePlacement };
};
